import { format, isValid, parseISO, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FicDashboardSummary } from 'src/services/wallet/ficDashboardSummary';
import {
  formatAmountBRL,
  formatCompactAxisCurrency,
  formatPercentageValue,
} from 'src/utils/number';

export type FollowingFicSort = 'criticality' | 'fidc-count' | 'alphabetical';

export function normalizeFicSearchTerm(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function compareNumberAscending(first: number, second: number): number {
  if (first === second) return 0;
  return first < second ? -1 : 1;
}

function compareNullableNumberDescending(
  first: number | null,
  second: number | null,
): number {
  if (first === null && second === null) return 0;
  if (first === null) return 1;
  if (second === null) return -1;
  if (first === second) return 0;
  return first > second ? -1 : 1;
}

function compareNames(
  first: FicDashboardSummary,
  second: FicDashboardSummary,
): number {
  const firstName = normalizeFicSearchTerm(first.fic.name ?? '');
  const secondName = normalizeFicSearchTerm(second.fic.name ?? '');

  if (!firstName && secondName) return 1;
  if (firstName && !secondName) return -1;
  if (firstName !== secondName) return firstName < secondName ? -1 : 1;

  const idOrder = compareNumberAscending(first.fic.id, second.fic.id);
  if (idOrder !== 0) return idOrder;

  const firstOperationId = first.fic.operationId ?? '';
  const secondOperationId = second.fic.operationId ?? '';
  if (firstOperationId === secondOperationId) return 0;
  return firstOperationId < secondOperationId ? -1 : 1;
}

function compareCriticality(
  first: FicDashboardSummary,
  second: FicDashboardSummary,
): number {
  return (
    compareNullableNumberDescending(
      first.alertBreakdown.critical,
      second.alertBreakdown.critical,
    ) ||
    compareNullableNumberDescending(
      first.alertBreakdown.attention,
      second.alertBreakdown.attention,
    ) ||
    compareNames(first, second)
  );
}

export function filterAndSortFollowingFics(
  cards: FicDashboardSummary[],
  searchTerm: string,
  sortBy: FollowingFicSort,
): FicDashboardSummary[] {
  const normalizedSearch = normalizeFicSearchTerm(searchTerm);
  const filteredCards = normalizedSearch
    ? cards.filter((card) =>
        normalizeFicSearchTerm(card.fic.name ?? '').includes(normalizedSearch),
      )
    : [...cards];

  return filteredCards.sort((first, second) => {
    if (sortBy === 'criticality') return compareCriticality(first, second);
    if (sortBy === 'fidc-count') {
      return (
        compareNumberAscending(
          second.persistedFidcCount,
          first.persistedFidcCount,
        ) || compareNames(first, second)
      );
    }
    return compareNames(first, second);
  });
}

export function formatDashboardCount(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'Indisponível';
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(
    value,
  );
}

export function formatDashboardMoney(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'Indisponível';
  const formatted =
    Math.abs(value) >= 1000000
      ? formatCompactAxisCurrency(value)
      : new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          notation: 'compact',
          compactDisplay: 'short',
          maximumFractionDigits: 1,
        }).format(value);

  return formatted
    .replace(/\s+mi$/u, 'M')
    .replace(/\s+bi$/u, 'B')
    .replace(/\u00a0/g, ' ');
}

export function formatDashboardFullMoney(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'Indisponível';
  return formatAmountBRL(value);
}

export function formatDashboardPercentage(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return 'Indisponível';
  return formatPercentageValue(value, 1);
}

export function formatDashboardCompetence(
  competence: string | null,
): string | null {
  if (!competence) return null;

  const parsedCompetence = parseISO(competence);
  if (!isValid(parsedCompetence)) return null;

  const formatMonth = (date: Date) =>
    format(date, 'MMM/yyyy', { locale: ptBR }).replace(/\./g, '');
  const normalizedCompetence = startOfMonth(parsedCompetence);

  return formatMonth(normalizedCompetence);
}
